import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal d'envoi d'email transactionnel via Resend.
 * Props :
 *   - defaultTo : liste d'emails pré-remplis (string[])
 *   - defaultSubject / defaultBody
 *   - contactId : si fourni, l'envoi est historisé dans le contact
 *   - context : 'contact' | 'bien' (pour titre)
 *   - onClose() / onSent(emailId)
 */
const EmailSendModal = ({
  defaultTo = [],
  defaultSubject = '',
  defaultBody = '',
  contactId = null,
  context = 'contact',
  onClose,
  onSent,
}) => {
  const [form, setForm] = useState({
    to: defaultTo.join(', '),
    subject: defaultSubject,
    body: defaultBody,
  });
  const [submitting, setSubmitting] = useState(false);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/email/status`);
        setConfigured(!!res.data?.configured);
      } catch {
        setConfigured(false);
      }
    })();
  }, []);

  const handleSubmit = async () => {
    const recipients = form.to.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Au moins un destinataire requis');
      return;
    }
    if (!form.subject.trim()) {
      toast.error('Le sujet est obligatoire');
      return;
    }
    if (!form.body.trim()) {
      toast.error('Le corps du message est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/email/send`, {
        to: recipients,
        subject: form.subject.trim(),
        body: form.body,
        contact_id: contactId,
      });
      toast.success('Email envoyé');
      onSent?.(res.data?.email_id);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="email-send-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <i className="bi bi-envelope-fill text-blue-600 text-lg"></i>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Envoyer un email</h3>
              <p className="text-xs text-slate-500">{context === 'bien' ? 'Depuis la fiche bien' : 'Depuis la fiche contact'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!configured && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <i className="bi bi-exclamation-triangle mr-1"></i>
              Service email non configuré côté serveur. Contactez l'administrateur.
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Destinataire(s) <span className="text-red-500">*</span>
              <span className="text-slate-400 font-normal ml-2">(séparés par virgule)</span>
            </label>
            <input
              type="text"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              placeholder="email@exemple.fr, autre@exemple.fr"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="email-to"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Sujet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Objet de l'email"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="email-subject"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={10}
              placeholder="Bonjour,&#10;&#10;..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              data-testid="email-body"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              <i className="bi bi-info-circle mr-1"></i>
              Une signature avec votre nom sera ajoutée automatiquement.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !configured || !form.to.trim() || !form.subject.trim() || !form.body.trim()}
            className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2"
            data-testid="email-send-btn"
          >
            <i className="bi bi-send"></i>
            {submitting ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSendModal;
